import { useCallback, useMemo, useRef } from "react"
import { debounce } from "lib/debounce"
import noop from "lib/noop"

// Collects per-thread "seen" signals from the visible comment rows and flushes them to the server as
// one batched mark-read call, so scrolling through a feed fires a single mutation rather than one per
// thread. Only threads currently flagged unread are tracked (re-seeing a read thread is a no-op), and
// `markThreadSeen` is stable so observers attached to rows never churn.
//
// `mark` receives `{ threadIds }`; the caller binds the share link / regime (recipient vs internal).
export function useShareLinkCommentReadTracker({ unreadThreadIds, mark }) {
    const unreadThreadSet = useMemo(() => new Set(unreadThreadIds ?? []), [unreadThreadIds])
    const state = useRef(null)
    if (!state.current) {
        state.current = { pending: new Set() }
        state.current.flush = debounce(() => flushPendingReadThreads(state.current), 800, { maxWait: 4000 })
    }
    state.current.unreadThreadSet = unreadThreadSet
    state.current.mark = mark

    const markThreadSeen = useCallback(function markThreadSeen(threadId) {
        const { current } = state
        if (!threadId || !current.unreadThreadSet.has(threadId) || current.pending.has(threadId)) return
        current.pending.add(threadId)
        current.flush()
    }, [])

    return { unreadThreadSet, markThreadSeen }
}

function flushPendingReadThreads(state) {
    const threadIds = [...state.pending]
    state.pending.clear()
    if (threadIds.length) Promise.resolve(state.mark?.({ threadIds })).catch(noop)
}
